import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Bot, FileText, TrendingUp, ScrollText } from "lucide-react";

const TYPE_ICONS = {
  agent: Bot, specialist: Bot, document: FileText,
  customer: TrendingUp, lead: TrendingUp, deal: TrendingUp, task: ScrollText,
};
const TYPE_COLORS = {
  agent: "#00E5FF", specialist: "#84CC16", document: "#EAB308",
  customer: "#10B981", lead: "#F97316", deal: "#F97316", task: "#94A3B8",
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const nav = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault(); setOpen(true);
      } else if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data.results);
        setActive(0);
      } catch { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const go = (r) => { setOpen(false); setQ(""); nav(r.link); };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && results[active]) go(results[active]);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="cmdk-overlay">
      <div className="w-full max-w-2xl bg-[var(--surface)] border border-[var(--accent)] shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="cmdk-panel">
        <div className="flex items-center gap-3 border-b border-[var(--border)] p-4">
          <Search className="w-4 h-4 text-[var(--accent)]" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Busca agentes, documentos, clientes, deals, tareas…"
            className="flex-1 bg-transparent outline-none text-sm text-white font-mono"
            data-testid="cmdk-input"
          />
          <span className="font-mono text-[10px] text-[var(--text-dim)]">ESC</span>
        </div>

        <div className="max-h-[400px] overflow-auto">
          {q && results.length === 0 && (
            <div className="p-6 text-center text-sm text-[var(--text-dim)] font-mono" data-testid="cmdk-empty">
              Sin resultados para "{q}"
            </div>
          )}
          {results.map((r, i) => {
            const Icon = TYPE_ICONS[r.type] || Bot;
            return (
              <button
                key={`${r.type}-${r.id}`}
                onClick={() => go(r)}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left flex items-center gap-3 p-3 border-l-2 ${
                  i === active ? "border-[var(--accent)] bg-[var(--surface-2)]" : "border-transparent hover:bg-[var(--surface-2)]/60"
                }`}
                data-testid={`cmdk-r-${i}`}
              >
                <Icon className="w-4 h-4" style={{ color: TYPE_COLORS[r.type] }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{r.title}</div>
                  <div className="text-xs text-[var(--text-dim)] truncate">{r.subtitle}</div>
                </div>
                <span className="font-mono text-[10px] text-[var(--text-dim)] uppercase">{r.type}</span>
              </button>
            );
          })}
        </div>

        <div className="border-t border-[var(--border)] p-3 flex items-center justify-between text-[10px] font-mono text-[var(--text-dim)]">
          <div>↑↓ navegar · ↵ abrir</div>
          <div>Cmd+K para abrir en cualquier momento</div>
        </div>
      </div>
    </div>
  );
}
